import { API, Auth } from 'aws-amplify'

const API_NAME = 'DonationsAPI'

// Helper to get auth headers
const getAuthHeaders = async () => {
  const session = await Auth.currentSession()
  return {
    Authorization: `Bearer ${session.getIdToken().getJwtToken()}`
  }
}

// API response types (snake_case from API)
interface FeatureFlagApiResponse {
  id: string
  key: string
  name: string
  description: string
  enabled: boolean
  targeted_emails: string[]
  created_at: string
  updated_at: string
}

// Create request (snake_case)
interface FeatureFlagCreateRequest {
  key: string
  name: string
  description?: string
  enabled: boolean
  targeted_emails?: string[]
}

// Update request (snake_case)
interface FeatureFlagUpdateRequest {
  name?: string
  description?: string
  enabled?: boolean
  targeted_emails?: string[]
}

// Frontend type (camelCase)
export interface FeatureFlag {
  id: string
  key: string
  name: string
  description: string
  enabled: boolean
  targetedEmails: string[]
  createdAt?: string
  updatedAt?: string
}

// Transform API response to frontend type
const transformApiResponse = (response: FeatureFlagApiResponse): FeatureFlag => ({
  id: response.id,
  key: response.key,
  name: response.name,
  description: response.description || '',
  enabled: response.enabled,
  targetedEmails: response.targeted_emails || [],
  createdAt: response.created_at,
  updatedAt: response.updated_at,
})

export const featureFlagsApi = {
  // List all feature flags
  async listFeatureFlags(): Promise<FeatureFlag[]> {
    const headers = await getAuthHeaders()
    const response: FeatureFlagApiResponse[] = await API.get(API_NAME, '/feature-flags', { headers })
    return response.map(transformApiResponse)
  },

  // Get a single feature flag by ID
  async getFeatureFlag(id: string): Promise<FeatureFlag> {
    const headers = await getAuthHeaders()
    const response: FeatureFlagApiResponse = await API.get(API_NAME, `/feature-flags/${id}`, { headers })
    return transformApiResponse(response)
  },

  // Create a new feature flag
  async createFeatureFlag(flag: FeatureFlag): Promise<FeatureFlag> {
    const headers = await getAuthHeaders()
    const body: FeatureFlagCreateRequest = {
      key: flag.key,
      name: flag.name,
      description: flag.description || undefined,
      enabled: flag.enabled,
      targeted_emails: flag.targetedEmails.length > 0 ? flag.targetedEmails : undefined,
    }
    const response: FeatureFlagApiResponse = await API.post(API_NAME, '/feature-flags', { headers, body })
    return transformApiResponse(response)
  },

  // Update an existing feature flag
  async updateFeatureFlag(id: string, updates: Partial<FeatureFlag>): Promise<FeatureFlag> {
    const headers = await getAuthHeaders()
    const body: FeatureFlagUpdateRequest = {}

    if (updates.name !== undefined) body.name = updates.name
    if (updates.description !== undefined) body.description = updates.description
    if (updates.enabled !== undefined) body.enabled = updates.enabled
    if (updates.targetedEmails !== undefined) body.targeted_emails = updates.targetedEmails

    const response: FeatureFlagApiResponse = await API.put(API_NAME, `/feature-flags/${id}`, { headers, body })
    return transformApiResponse(response)
  },

  // Delete a feature flag
  async deleteFeatureFlag(id: string): Promise<void> {
    const headers = await getAuthHeaders()
    await API.del(API_NAME, `/feature-flags/${id}`, { headers })
  },
}

export default featureFlagsApi
